import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, Observable, skip } from 'rxjs';

type EventType =
    | 'closeUserModal'
    | 'changeLanguage'
    | 'activateExpando'
    | 'save'
    | 'publish'
    | 'revert'
    | 'pageMainButtonAction'
    | 'modalMainButtonAction'
    | 'dynamicFormUpdate'
    | 'dynamicFormUpdateValidity'
    | 'refreshDynamicFormValues'
    | 'featureGroupSearch'
    | 'listing:compare'
    | 'listing:bulkEdit:save'
    | 'listing:bulkEdit:getSavedDiff:response'
    | 'listing:bulkEdit:getSavedDiff:request'
    | 'listing:widgets:summary:search'
    | 'listing:version'
    | 'tabChanged'
    | 'logout'
    | 'tokenChange'
    | 'scrollDebounced'
    | 'scrollThrottled'
    | 'modalOpen'
    | 'notification'
    | 'listing:fieldMappings'
    | 'bookingQuickConnectFinished'
    | 'shouldRefreshFormStructure'
    | 'clearPreservedValuesMap'
    | 'listing:statusUpdate'
    | 'listing:visualsStatusUpdate'
    | 'listing:distributionTrackingStatusUpdate'
    | 'confirmedListingActionModal'
    | 'listing:reload'
    | 'listing:userPreferences:restrictiveUiChannelsUpdate'
    | 'closeUserGroupModal'
    | 'listing:publishingTakingTooLong'
    | 'listing:snapshotStatusUpdate'
    | 'listing:bulkEdit:saveAndActivate'
    | 'listing:bulkEdit:openListingList'
    | 'updateSupplierUserVisualPermissions';

@Injectable({
    providedIn: 'root',
})
export class EventBusService {
    private registeredEventTypes: { [key: string]: BehaviorSubject<unknown> } = {};

    constructor() {}

    public getObservableForEventStream(
        eventType: 'listing:widgets:summary:search',
        includeInitialValue?: boolean,
    ): Observable<{ path: string; level: number; value: string }>;
    public getObservableForEventStream(eventType: string, includeInitialValue?: boolean): Observable<unknown>;
    public getObservableForEventStream(eventType: EventType, includeInitialValue = false): Observable<unknown> {
        if (!this.registeredEventTypes[eventType]) {
            this.registeredEventTypes[eventType] = new BehaviorSubject(undefined as unknown);
        }

        const eventValue = this.registeredEventTypes[eventType].asObservable();

        return includeInitialValue ? eventValue : eventValue.pipe(skip(1));
    }

    public getEventStreamCurrentValue(eventType: EventType): unknown {
        if (!this.registeredEventTypes[eventType]) {
            return undefined;
        }

        return this.registeredEventTypes[eventType].value;
    }

    public dispatch(eventType: EventType, data?: unknown, registerEventTypeIfNonExistent?: boolean): void {
        if (this.registeredEventTypes[eventType]) {
            this.registeredEventTypes[eventType].next(data);
        } else {
            if (registerEventTypeIfNonExistent) {
                this.registeredEventTypes[eventType] = new BehaviorSubject(data);
            } else if (!environment.production) {
                console.warn(`There is no event of type ${eventType} registered on the Event Bus`);
            }
        }
    }

    public unregisterFromEventStreams() {
        // TODO: make a way to check for subscriptions on a given subject and delete that subject if it's no longer active
    }
}
